<template>
  <div>
    <copy-features-dialog
      name="investment-area-search"
      :is-visible.sync="isCopyVisible"
      :is-loading.sync="isCopyLoading"
      input-layer-id="investment-area-search"
      :geojson="results"
      :geojson-copy-attributes="resultsAttributes"
      v-bind="copyWithOzeProjectBind"
    >
      <template #upperForm>
        <v-form v-model="projectValid" ref="projectForm">
          <v-row no-gutters>
            <v-col :cols="hasAddProjectButton ? 11 : 12">
              <main-input-dialog-row v-model="projectId" v-bind="copyToProjectBind" left-col="6" />
            </v-col>
            <v-col cols="1" class="pt-1" v-if="hasAddProjectButton">
              <base-rounded-button
                icon="mdi-plus"
                size="x-small"
                color="success"
                :tooltip="$i18n.t('sidebar.searchArea.addProject')"
                @click="toggleProjectAdding"
              />
            </v-col>
          </v-row>
        </v-form>
      </template>
    </copy-features-dialog>
    <main-input-dialog
      ref="select-project-layer-dialog"
      v-if="projectLayerSelectionVisible"
      :is-visible.sync="projectLayerSelectionVisible"
      :title-translation="$i18n.t('dialog.title.selectLayer')"
      :inputs="projectLayerInput"
      agree-text="dialog.agreeChoose"
      disable-default-form-bottom-padding
      @agree="toggleProjectFeatureCard"
    >
      <template #[`projectLayer.input`]="data">
        <v-col cols="6">
          <data-input v-model="projectLayer" v-bind="data" />
        </v-col>
      </template>
    </main-input-dialog>
    <add-edit-feature-dialog
      v-if="projectFeatureCardVisible"
      :is-visible.sync="projectFeatureCardVisible"
      v-bind="projectFeatureCard"
      @done="onProjectAdded"
      :feature-metadata="{ properties: {} }"
    />
  </div>
</template>

<script>
import { get, call } from 'vuex-pathify';

import MainInputDialog from '@/components/MainInputDialog';
import MainInputDialogRow from '@/components/MainInputDialogRow';
import AddEditFeatureDialog from '@/components/AddEditFeatureDialog';

export default {
  name: 'OzeProjectCopyDialog',
  components: {
    AddEditFeatureDialog,
    MainInputDialog,
    MainInputDialogRow,
    CopyFeaturesDialog: () => import('@/components/CopyFeaturesDialog'),
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    results: {
      type: Object,
      default: undefined,
    },
    resultsAttributes: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    isCopyLoading: false,
    projectsLoading: false,
    projectValid: false,
    projectLayerSelectionVisible: false,
    projectLayer: null,
    projectFeatureCardVisible: false,
    projectFeatureCard: null,
    projectId: null,
  }),
  computed: {
    layers: get('layers/layers'),
    ozeParcelsDatasource: get('admin/modulesMapping@oze.oze_parcels_datasource.datasource'),
    ozeProjectsDatasource: get('admin/modulesMapping@oze.oze_projects_datasource.datasource'),
    ozeProjectsDatasourceGeometryType: get('layers/metadata@:ozeProjectsDatasource.geometry_type'),
    ozeParcelsDatasourceIdName: get('layers/metadata@:ozeParcelsDatasource.attributes_schema.id_name'),
    ozeParcelsDatasourceGeometryName: get('layers/metadata@:ozeParcelsDatasource.attributes_schema.geometry_name'),
    isCopyVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    ozeParcelsLayers() {
      return Object.values(this.layers)
        .filter(layer => layer.data_source_name === this.ozeParcelsDatasource)
        .map(layer => ({
          value: layer.id,
          text: layer.name,
        }));
    },
    ozeProjectsLayers() {
      return Object.values(this.layers)
        .filter(layer => layer.data_source_name === this.ozeProjectsDatasource)
        .map(layer => ({
          value: layer.id,
          text: layer.name,
        }));
    },
    projectLayerInput() {
      return [
        {
          dataType: { name: 'select' },
          name: 'projectLayer',
          cols: '12',
          translationPath: 'default.layer',
          items: this.ozeProjectsLayers,
          rules: [v => !!v || 'rules.required'],
        },
      ];
    },
    copyWithOzeProjectBind() {
      return {
        dialogTitle: this.$i18n.t('sidebar.searchArea.addToOzeProject'),
        layerTitle: this.$i18n.t('sidebar.searchArea.copyFeaturesLayerTitle'),
        hideLayerSubtitle: true,
        mappingAttributesTitle: this.$i18n.t('sidebar.searchArea.mappingAttributesTitle'),
        agreeText: 'dialog.agreeAssign',
        customTargetLayers: this.ozeParcelsLayers,
        customIsValid: this.projectValid,
        customCopyObjectsAction: this.copyWithOzeProject,
        formCols: this.hasAddProjectButton ? 11 : 12,
      };
    },
    hasAddProjectButton() {
      return this.ozeProjectsLayers?.length && !this.ozeProjectsDatasourceGeometryType;
    },
    copyToProjectBind() {
      return {
        name: 'projectId',
        dataType: { name: 'select' },
        items: this.projects,
        itemValue: 'id',
        itemText: 'desc',
        cols: 12,
        straightTitle: this.$i18n.t('sidebar.searchArea.project'),
        loading: this.projectsLoading,
        rules: [v => !!v || v == '0' || 'rules.required'],
      };
    },
  },
  watch: {
    isCopyVisible(nV) {
      if (!nV) this.projectId = null;
      this.$refs.projectForm?.resetValidation();
    },
    projectLayer(nV) {
      if (nV) localStorage.setItem('oze_project_layer', nV);
    },
  },
  methods: {
    addRelation: call('relations/addRelation'),
    editMultipleDataSourceFeatures: call('layers/editMultipleDataSourceFeatures'),
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    toggleProjectAdding() {
      if (this.ozeProjectsLayers?.length === 1) {
        this.projectLayer = this.ozeProjectsLayers[0].value;
        this.toggleProjectFeatureCard();
      } else {
        const projectLayer = +localStorage.getItem('oze_project_layer');
        if (projectLayer && this.ozeProjectsLayers.some(layer => layer.value === projectLayer))
          this.projectLayer = projectLayer;
        else this.projectLayer = null;
        this.projectLayerSelectionVisible = true;
      }
    },
    toggleProjectFeatureCard(projectLayer = this.projectLayer) {
      this.projectLayerSelectionVisible = false;
      this.projectFeatureCard = {
        title: this.$i18n.t('dialog.title.addNewObjectName', { layer_name: this.layers[projectLayer].name }),
        featureData: {
          datasource: this.ozeProjectsDatasource,
          layer: projectLayer,
        },
      };
      this.projectFeatureCardVisible = this.projectFeatureCardVisible = true;
    },
    async onProjectAdded(closeDialog, metadata, newItemId) {
      await this.getProjects(newItemId);
      closeDialog();
    },
    async getProjects(setProjectId = null) {
      this.projectsLoading = true;
      try {
        const features = await this.getDataSourceFeatures({
          dataSource: this.ozeProjectsDatasource,
          idsDescsOnly: true,
        });
        this.projects = features;
        if (setProjectId || setProjectId === 0) this.projectId = setProjectId;
      } finally {
        this.projectsLoading = false;
      }
    },
    async copyWithOzeProject(geojson, layerId) {
      const payload = {
        data_source_name: this.ozeParcelsDatasource,
        insert: geojson.features.map(feature => {
          return {
            ...feature.properties,
            [this.ozeParcelsDatasourceGeometryName]: { ...feature.geometry, crs: geojson.crs },
          };
        }),
        params: { layer_id: layerId },
      };
      const r = await this.editMultipleDataSourceFeatures(payload);
      const newFeaturesIds = r.data.data.insert?.map(item => item[this.ozeParcelsDatasourceIdName]);
      await this.addRelation({
        data_source_name: this.ozeProjectsDatasource,
        feature_id: this.projectId,
        features: newFeaturesIds.map(featureId => {
          return {
            data_source_name: this.ozeParcelsDatasource,
            feature_id: featureId,
          };
        }),
      });
    },
  },
  mounted() {
    this.getProjects();
  },
};
</script>
